import { ImagesHolder } from "../../image_loader/images_holder.js";
import { GlobalSettings, UiBackendKey } from "../../global_settings.js";

export const DOM_UI_KEYS = {
  DIALOG: "dialog",
  OVERLAY: "overlay",
  TOOLTIP: "tooltip",
  NAVIGATION: "navigation",
  LOADING: "loading",
} as const;

export const DOM_BACKEND_SCREENS: ReadonlySet<string> = new Set( [
  "menu", "shop", "leaderboard", "daily_tasks", "feedback", "adventure",
  "daily_challenge", "chain_offer", "one_plus_one_offer", "one_plus_two_offer", "game",
  DOM_UI_KEYS.DIALOG, DOM_UI_KEYS.OVERLAY, DOM_UI_KEYS.TOOLTIP, DOM_UI_KEYS.NAVIGATION, DOM_UI_KEYS.LOADING,
] );

export function resolveUiBackend( screen: string ): UiBackendKey
{
  return GlobalSettings.UI_BACKEND === "dom" && DOM_BACKEND_SCREENS.has( screen ) ? "dom" : "canvas";
}

export const DOM_INDIVIDUAL_TRANSFORMS: boolean =
  typeof CSS !== "undefined" && typeof CSS.supports === "function" && CSS.supports( "translate", "1px" );

export const MIN_DOM_SCALE = 0.01;

export function isDomScaleCollapsed( x: number, y: number ): boolean
{
  return Math.min( Math.abs( x ), Math.abs( y ) ) < MIN_DOM_SCALE;
}

export function domScaleCss( x: number, y: number ): string
{
  const clamp = ( v: number ) => Math.abs( v ) >= MIN_DOM_SCALE ? v : ( v < 0 ? -MIN_DOM_SCALE : MIN_DOM_SCALE );
  return `${clamp( x )} ${clamp( y )}`;
}

export const DOM_ANIMATIONS_SUPPORTED: boolean =
  DOM_INDIVIDUAL_TRANSFORMS && typeof Element !== "undefined" && typeof Element.prototype.animate === "function";

export const DOM_LINEAR_EASING_SUPPORTED: boolean =
  typeof CSS !== "undefined" && typeof CSS.supports === "function"
  && CSS.supports( "animation-timing-function", "linear(0, 1)" );

const MIRRORED_STYLE_PROPS = [ "display", "left", "top", "width", "height", "zIndex", "opacity" ] as const;

function mirrorCanvasStyle( canvas: HTMLCanvasElement, host: HTMLDivElement ): void
{
  const from = canvas.style;
  const to = host.style;
  for ( const prop of MIRRORED_STYLE_PROPS )
  {
    let value = from[prop];
    if ( prop === "zIndex" && !value )
      value = getComputedStyle( canvas ).zIndex;
    if ( prop === "display" )
      value = "block";
    if ( to[prop] !== value )
      to[prop] = value;
  }
  const visibility = from.display === "none" ? "hidden" : "";
  if ( to.visibility !== visibility )
  {
    to.visibility = visibility;
    syncHostAnimations( host, visibility === "hidden" );
  }
}

const host_paused_animations = new WeakSet<Animation>();

interface DomTimedAnimation extends Animation
{
  dom_started_at?: number;
}

export function stampDomAnimationStart( anim: Animation | null, started_at: number ): Animation | null
{
  if ( anim )
    ( anim as DomTimedAnimation ).dom_started_at = started_at;
  return anim;
}

export function resumeDomAnimation( anim: Animation ): void
{
  const started_at = ( anim as DomTimedAnimation ).dom_started_at;
  if ( started_at === undefined )
  {
    anim.play();
    return;
  }
  const elapsed = Math.max( 0, performance.now() - started_at );
  const end = anim.effect?.getComputedTiming().endTime;
  if ( end !== undefined && elapsed >= Number( end ) )
  {
    anim.finish();
    return;
  }
  anim.currentTime = elapsed;
  anim.play();
}

export function pauseForHiddenHost( anim: Animation ): void
{
  anim.pause();
  host_paused_animations.add( anim );
}

function syncHostAnimations( host: HTMLDivElement, hidden: boolean ): void
{
  if ( typeof host.getAnimations !== "function" )
    return;
  for ( const anim of host.getAnimations( { subtree: true } ) )
  {
    if ( hidden && anim.playState === "running" )
    {
      anim.pause();
      host_paused_animations.add( anim );
    }
    else if ( !hidden && anim.playState === "paused" && host_paused_animations.has( anim ) )
    {
      host_paused_animations.delete( anim );
      resumeDomAnimation( anim );
    }
  }
}

const hosts = new WeakMap<HTMLCanvasElement, HTMLDivElement>();
let styles_injected = false;

function ensureDomStyles(): void
{
  if ( styles_injected )
    return;
  styles_injected = true;
  const style = document.createElement( "style" );
  style.id = "ui-dom-backend-style";
  style.textContent = ".ui-dom-dim{filter:brightness(var(--ui-dom-dim,0.22))!important}";
  document.head.appendChild( style );
}

export function getDomHost( canvas: HTMLCanvasElement ): HTMLDivElement
{
  let host = hosts.get( canvas );
  if ( host )
    return host;

  ensureDomStyles();
  host = document.createElement( "div" );
  host.className = "ui-dom-host";
  host.dataset.canvas = canvas.id;
  const s = host.style;
  s.position = "fixed";
  s.pointerEvents = "none";
  s.overflow = "hidden";
  s.contain = "layout style";
  s.willChange = "transform";
  mirrorCanvasStyle( canvas, host );
  canvas.parentNode?.insertBefore( host, canvas );

  const mirrored = host;
  const observer = new MutationObserver( () => mirrorCanvasStyle( canvas, mirrored ) );
  observer.observe( canvas, { attributes: true, attributeFilter: [ "style" ] } );

  clearCanvasOnce( canvas );
  hosts.set( canvas, host );
  return host;
}

export function showDomHostForRaster( canvas: HTMLCanvasElement, opacity: number ): void
{
  const host = getDomHost( canvas );
  host.style.opacity = `${opacity}`;
  host.style.visibility = "";
}

export function resyncDomHost( canvas: HTMLCanvasElement ): void
{
  const host = hosts.get( canvas );
  if ( !host )
    return;
  host.style.visibility = "";
  mirrorCanvasStyle( canvas, host );
}

const canvases_with_context = new WeakSet<HTMLCanvasElement>();

export function markCanvasHasContext( canvas: HTMLCanvasElement ): void
{
  canvases_with_context.add( canvas );
}

export function canvasHasContext( canvas: HTMLCanvasElement ): boolean
{
  return canvases_with_context.has( canvas );
}

export function clearCanvasOnce( canvas: HTMLCanvasElement ): void
{
  if ( !canvases_with_context.has( canvas ) ) return;
  const ctx = canvas.getContext( "2d" );
  if ( !ctx ) return;
  ctx.save();
  ctx.setTransform( 1, 0, 0, 1, 0, 0 );
  ctx.clearRect( 0, 0, canvas.width, canvas.height );
  ctx.restore();
}

export function snapToDevicePx( v: number ): number
{
  const dpr = window.devicePixelRatio || 1;
  return Math.round( v * dpr ) / dpr;
}

export const DOM_SYNC_DECODE_MIN_BYTES = 500 * 1024;

export function mountDomSyncImage( node: HTMLElement, img: HTMLImageElement | null, url: string ): HTMLImageElement
{
  let el = img;
  if ( !el )
  {
    el = document.createElement( "img" );
    el.className = "ui-dom-img";
    el.decoding = "sync";
    el.draggable = false;
    el.alt = "";
    const s = el.style;
    s.position = "absolute";
    s.left = "0";
    s.top = "0";
    s.width = "100%";
    s.height = "100%";
    s.maxWidth = "none";
    s.pointerEvents = "none";
    s.userSelect = "none";
  }
  if ( el.parentNode !== node )
    node.insertBefore( el, node.firstChild );
  if ( el.getAttribute( "src" ) !== url )
    el.src = url;
  return el;
}

const CSS_URL_RE = /url\(\s*["']?([^"')]+)["']?\s*\)/g;

function addCssUrls( css: string, out: Set<string> ): void
{
  if ( !css || css.indexOf( "url(" ) < 0 )
    return;
  for ( const m of css.matchAll( CSS_URL_RE ) )
    out.add( m[1] );
}

export function collectDomImageUrls( root: Element, out: Set<string> ): void
{
  const visit = ( el: Element ): void =>
  {
    const s = ( el as HTMLElement ).style;
    if ( s )
    {
      addCssUrls( s.backgroundImage, out );
      addCssUrls( s.borderImageSource, out );
    }
    if ( el instanceof HTMLImageElement )
    {
      const src = el.getAttribute( "src" );
      if ( src ) out.add( src );
    }
  };
  visit( root );
  for ( const el of root.querySelectorAll( ".ui-dom-el, .ui-dom-skin, .ui-dom-backdrop, img" ) )
    visit( el );
}

interface DomDecodeEntry
{
  img: HTMLImageElement;
  ready: Promise<unknown>;
}

let decode_gate = new Map<string, DomDecodeEntry>();

export async function decodeDomImageUrls( urls: Iterable<string>, timeout_ms: number ): Promise<void>
{
  const next = new Map<string, DomDecodeEntry>();
  const pending: Promise<unknown>[] = [];
  for ( const url of urls )
  {
    let entry = decode_gate.get( url );
    if ( !entry )
    {
      let img = ImagesHolder.getDomImageByUrl( url );
      if ( !img )
      {
        img = new Image();
        img.src = url;
      }
      entry = { img, ready: img.decode().catch( () => null ) };
    }
    next.set( url, entry );
    pending.push( entry.ready );
  }
  decode_gate = next;
  if ( pending.length === 0 )
    return;
  await Promise.race( [ Promise.all( pending ), new Promise( resolve => setTimeout( resolve, timeout_ms ) ) ] );
}
